<script lang="ts">
  import {onMount} from 'svelte';
  import {httpClient} from '../../../../../utils/http-client';
  import {user, isMobile} from '@/store';
  import Swal from 'sweetalert2';
  import {Video} from '@/types/project';
  import {download} from '@/utils/common';
  import CarouselElement from '@/components/carousel/CarouselElement.svelte';
  import DropdownButton from '@/components/button/DropdownButton/DropdownButton.svelte';
  import {parseCarousel} from './Carousel';

  export let selectedVideo: Video | null;

  const downloadOptions = [
    {
      label: 'as Text',
      icon: 'fa-solid fa-file',
      value: 'txt'
    },
    {
      label: 'as PDF',
      icon: 'fa-solid fa-file-pdf',
      value: 'pdf'
    }
  ];
  const type = 'Social Carousel';
  let isLoading = true;
  let carousels: any[] = [];
  // let verses: any[] = [];

  const handleDownload = async (extension: 'txt' | 'pdf' = 'txt') => {
    const filename = `[${type}] ${selectedVideo?.title}`;
    const text = parseCarousel(carousels, extension);

    await download(filename, text, type, extension);
  };

  async function regenerate() {
    if (!selectedVideo) throw 'selectedVideo is null';
    isLoading = true;

    await httpClient
      .get(`/sermon-summary/${selectedVideo.id}/carousels/regenerate`)
      .then(response => {
        carousels = response?.data?.result?.carousel ? response.data.result?.carousel : [];
        // verses = response.data.verses;
        isLoading = false;
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        isLoading = false;
      });
  }

let scrollContainers: HTMLElement[] = [];

// Track scroll state per carousel
let scrollStates: { atStart: boolean; atEnd: boolean }[] = [];

const scrollAmount = 264; // 240px + 24px gap

function updateScrollState(index: number) {
  const container = scrollContainers[index];
  if (!container) return;

  const atStart = container.scrollLeft <= 0;
  const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;

  scrollStates[index] = { atStart, atEnd };
  scrollStates = [...scrollStates]; // trigger reactivity
}

function scrollToLeft(index: number) {
  const container = scrollContainers[index];
  if (container) {
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }
}

function scrollToRight(index: number) {
  const container = scrollContainers[index];
  if (container) {
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
}

// Set up scroll listeners
onMount(() => {
  httpClient
      .get(`/sermon-summary/${selectedVideo?.id}/carousels`)
      .then(response => {
        //console.log('carousels response', response);
        carousels = response?.data?.result?.carousel ? response.data.result?.carousel : [];
        console.log(carousels);
        // verses = response?.data?.verses ? response.data.verses : [];
        isLoading = false;
      })
      .catch(error => {
        console.error('Error fetching carousels:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch carousels. Please try again later.',
          confirmButtonText: 'OK'
        });
        isLoading = false;
      }).finally(() => {
        scrollContainers.forEach((container, index) => {
          updateScrollState(index);
          const onScroll = () => updateScrollState(index);
          container.addEventListener('scroll', onScroll);
          // Cleanup
          return () => container.removeEventListener('scroll', onScroll);
        });
      });
});
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center justify-between border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">{type}</h2>
    {/if}
    <div class="flex w-full justify-center space-x-2 py-2 lg:justify-end lg:py-0">
      {#if $user.isImpersonated || $user.isAdmin}
        <button
          type="button"
          on:click={() => regenerate()}
          class="w-full rounded-md px-3 lg:w-auto py-1{isLoading
            ? 'disabled cursor-progress bg-zinc-400 text-white'
            : 'hoversermonbg border border-primary bg-white text-primary hover:border-0 hover:text-white'}"
          disabled={isLoading}><i class="fa-solid fa-repeat"></i>&nbsp; Regenerate</button
        >
      {/if}
      <DropdownButton
        on:click={() => handleDownload()}
        on:optionClick={option => handleDownload(option.detail.value)}
        disabled={isLoading}
        options={downloadOptions}
      >
        <span class="flex items-center gap-x-2">
          <i class="fa-solid fa-download" />Download
        </span>
      </DropdownButton>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoading}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          />
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Carousels...</h1>
        </div>
      </div>
    {:else if carousels}
    <div class="bg-gray-100 font-normal text-base leading-6 tracking-normal px-3 py-4 rounded-xl"><i class="fa-solid fa-circle-info cursor-pointer mr-2 text-lg text-gray-300" aria-hidden="true"></i> Download the slides and create visuals with your preferred tool. Stay tuned: our own carousel generator is coming soon!</div>
    <p class="font-normal text-sm leading-6 tracking-normal py-2 text-dark-400">Don’t like the output? Click on the <strong>Help</strong> button below to contact support.</p>
      <div class="flex h-full w-full justify-center font-primary">
        <div class="h-full w-full">
          <div class="flex w-full flex-col">
            {#each carousels as carousel, index}
            <div class="my-4 w-full">
              <div class="my-2 flex items-center gap-2 justify-between">
                <p class="text-lg font-semibold text-dark">
                  Carousel {index + 1}: "{carousel.title}"
                </p>
                <div class="">
                  <button
                    class="rounded-full p-2 text-gray-600 hover:bg-gray-300 disabled:opacity-40"
                    on:click={() => scrollToLeft(index)}
                    disabled={scrollStates[index]?.atStart}
                  >
                    <i class="fa fa-solid fa-chevron-left"></i>
                  </button>
                  <button
                    class="rounded-full p-2 text-gray-600 hover:bg-gray-300 disabled:opacity-40"
                    on:click={() => scrollToRight(index)}
                    disabled={scrollStates[index]?.atEnd}
                  >
                    <i class="fa fa-solid fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              <div
                class="scrollbar-hide w-full overflow-x-auto"
                bind:this={scrollContainers[index]}
                on:scroll={() => updateScrollState(index)}
              >
                <div class="flex w-max flex-row gap-6 py-2 font-medium">
                  {#each carousel.slides as slide, idx}
                    <CarouselElement carousel={slide} index={idx} />
                  {/each}
                </div>
              </div>
            </div>
          {/each}
          </div>
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Sermon Summary Found</h1>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">

  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for Firefox */
  .scrollbar-hide {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    cursor: grab;
    /* user-select: none; */
  }
</style>
